"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { formatPrice } from "@/lib/format-price";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type PriceBlok = SbBlokData & {
  name?: string;
  description?: string;
  price_ie?: string;
  price_uk?: string;
  monthly_price_ie?: string;
  monthly_price_uk?: string;
  features?: string;
  learn_more_link?: StoryblokLink;
  learn_more_label?: string;
};

function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
      className="text-teal-07 mt-[3px] size-4.5 shrink-0"
    >
      <path
        d="M3 9.5l3.5 3.5L15 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Price({ blok }: { blok: PriceBlok }) {
  const ie = formatPrice(blok.price_ie, "ie");
  const uk = formatPrice(blok.price_uk, "uk");
  const monthlyIe = formatPrice(blok.monthly_price_ie, "ie");
  const monthlyUk = formatPrice(blok.monthly_price_uk, "uk");
  const features = (blok.features || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const region = regionFromPathname(usePublicPathname());
  const learnMore = resolveLink(blok.learn_more_link, region);
  const learnMoreLabel = blok.learn_more_label || "View treatment";

  return (
    <section {...storyblokEditable(blok)} className="bg-teal-01 flex justify-center px-5 py-16">
      <div className="text-grey-09 ring-teal-04 w-full max-w-[420px] rounded-[20px] bg-white p-6 ring-1">
        {blok.name && <div className="text-lg font-bold tracking-[0.02em]">{blok.name}</div>}
        {blok.description && (
          <p className="text-grey-07 mt-2 text-sm leading-[1.4]">{blok.description}</p>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <div className="text-grey-06 text-xs font-bold tracking-[0.08em] uppercase">
              Ireland
            </div>
            {ie && (
              <div className="text-teal-07 mt-1 text-[28px] leading-none font-bold tracking-[0.02em]">
                {ie}
              </div>
            )}
            {monthlyIe && <div className="text-grey-07 mt-1 text-sm">{monthlyIe}</div>}
          </div>
          <div>
            <div className="text-grey-06 text-xs font-bold tracking-[0.08em] uppercase">
              United Kingdom
            </div>
            {uk && (
              <div className="text-teal-07 mt-1 text-[28px] leading-none font-bold tracking-[0.02em]">
                {uk}
              </div>
            )}
            {monthlyUk && <div className="text-grey-07 mt-1 text-sm">{monthlyUk}</div>}
          </div>
        </div>

        {features.length > 0 && (
          <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {features.map((feat, i) => (
              <li key={`${i}-${feat}`} className="flex items-start gap-2">
                <CheckIcon />
                <span className="text-grey-08 text-base leading-tight">{feat}</span>
              </li>
            ))}
          </ul>
        )}

        {learnMore.href !== "#" && (
          <a
            href={learnMore.href}
            {...(learnMore.target ? { target: learnMore.target } : {})}
            rel={learnMore.target === "_blank" ? "noopener noreferrer" : undefined}
            className="text-teal-07 mt-6 inline-flex w-fit items-center gap-1.75 py-2.5 text-base font-bold hover:underline"
          >
            {learnMoreLabel}
            <ArrowIcon />
          </a>
        )}
      </div>
    </section>
  );
}
